import { createStore } from 'zustand/vanilla'
import type { RailNetwork } from '@traintopia/contracts'
import { api } from '../services/api'
import { registerRailRoutes } from '../ui/colors'

// ─────────────────────────────────────────────────────────────────────────────
// LIRR + Metro-North's static network: branches, stations and track lines.
//
// The ferry store's shape exactly (see store/ferry.ts): one lazy load, the first time
// anything asks — the map when the Rail filter is picked, an alert bullet naming a branch.
// ~260 KB, so it is never fetched on a session that doesn't look at the railroads. Loading
// it also REGISTERS the branch colours with `ui/colors`; nothing rail-coloured should draw
// before `network` is set, or a badge is cached in the fallback blue for the session.
//
// The trains themselves arrive on the vehicle socket as ordinary `VehiclePosition`s with
// `type: 'rail'`, into the transit store like every vehicle.
// ─────────────────────────────────────────────────────────────────────────────

interface RailState {
  network: RailNetwork | null
  loading: boolean
  /** Load the network if it isn't loaded or loading. Safe to call on every filter change. */
  ensureLoaded: () => void
}

const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000]

export const useRailStore = createStore<RailState>((set, get) => {
  let attempt = 0
  const load = () => {
    set({ loading: true })
    api
      .getRailNetwork()
      .then((network) => {
        // The backend answers 503 with an EMPTY network when both zips are down; treat that
        // as a failure to retry, not a loaded network with no trains.
        if (!network.routes.length) throw new Error('empty rail network')
        registerRailRoutes(network.routes)
        attempt = 0
        set({ network, loading: false })
      })
      .catch(() => {
        // A few spaced retries, then stand down until the next `ensureLoaded`.
        if (attempt >= RETRY_DELAYS_MS.length) {
          attempt = 0
          set({ loading: false })
          return
        }
        setTimeout(load, RETRY_DELAYS_MS[attempt++])
      })
  }
  return {
    network: null,
    loading: false,
    ensureLoaded: () => {
      const s = get()
      if (s.network || s.loading) return
      load()
    },
  }
})
